import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api } from "../api";
import { city, relative } from "../components/ui";

// The question: is every environment of every project answering right now, and if not, why? And is
// anyone still using it?

export type CheckState = "up" | "degraded" | "down" | "unknown";

export interface ProbeView { url: string; at: string; status: number | null; latencyMs: number | null; error: string | null; certDays: number | null }

export interface CheckRow {
  project: string;
  projectName: string;
  env: string;
  envId: string;
  onLab: boolean;
  region: string;
  state: CheckState;
  reasons: string[];
  urls: ProbeView[];
  unprobed: string[];
  checks: { url: string; expect: number | null; probe: ProbeView | null }[];
  containers: { healthy: number; expected: number; total: number } | null;
  probed: boolean; // false: "up" rests on the server or containers alone, no URL answered
  lastUsed: { recorded: true; lastRequestAt: string | null; requests24h: number } | { recorded: false };
  lastDeploy: { sha: string | null; at: string | null };
  uptime24h: number | null;
  uptime7d: number | null;
  strip: (number | null)[];
}

export interface Checks { checkedAt: string; rows: CheckRow[] }

export function useChecks(project?: string) {
  return useQuery({
    queryKey: ["checks", project ?? ""],
    queryFn: () => api<Checks>(`/checks${project ? `?project=${encodeURIComponent(project)}` : ""}`),
    refetchInterval: 60_000,
  });
}

const WORDS: Record<CheckState, string> = { up: "Up", degraded: "Degraded", down: "Down", unknown: "Unknown" };

export function StateMark({ state }: { state: CheckState }) {
  return <span className={`health ${state}`}><span className="dot" />{WORDS[state]}</span>;
}

/** The last 24 hours, one cell per hour: solid when every probe answered, red when any failed, outlined when none ran. */
export function Strip({ strip }: { strip: (number | null)[] }) {
  return (
    <span className="strip" role="img" aria-label={`Last 24 hours: ${strip.filter((s) => s !== null && s < 1).length} hours with failures`}>
      {strip.map((s, i) => (
        <span key={i} className={`cell ${s === null ? "none" : s >= 1 ? "ok" : s > 0 ? "some" : "fail"}`} title={`${24 - i} h ago: ${s === null ? "not probed" : `${Math.round(s * 100)}% of probes answered`}`} />
      ))}
    </span>
  );
}

export const pct = (v: number | null) => (v === null ? "—" : v >= 0.9995 ? "100%" : `${(Math.floor(v * 1000) / 10).toFixed(1)}%`);
const host = (u: string) => u.replace(/^https?:\/\//, "").replace(/\/$/, "");

export function LastUsed({ u }: { u: CheckRow["lastUsed"] }) {
  if (!u.recorded) return <span className="muted">not recorded</span>;
  if (!u.lastRequestAt) return <span className="muted">no real use in the log</span>;
  return (
    <>
      <div title={new Date(u.lastRequestAt).toLocaleString()}>{relative(u.lastRequestAt)}</div>
      <div className="cell-sub num">{u.requests24h.toLocaleString()} in 24 h</div>
    </>
  );
}

function Probe({ p, expect }: { p: ProbeView; expect?: number | null }) {
  const bad = expect === undefined
    ? p.status === null || p.status >= 500 || p.error
    : p.status === null || !!p.error || (expect !== null ? p.status !== expect : p.status >= 400);
  return (
    <div className="probe">
      <a href={p.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mono truncate">{host(p.url)}</a>
      <span className={`num ${bad ? "error" : "muted"}`} title={p.error ?? `probed ${relative(p.at)}`}>
        {p.status ?? "no answer"}{expect !== undefined && expect !== null && p.status !== expect ? ` (want ${expect})` : ""}{p.latencyMs !== null ? ` · ${p.latencyMs} ms` : ""}{p.certDays !== null ? ` · cert ${p.certDays} d` : ""}
      </span>
    </div>
  );
}

type SortKey = "env" | "state" | "used" | "deploy" | "uptime";
const ORDER: Record<CheckState, number> = { down: 0, degraded: 1, unknown: 2, up: 3 };
const time = (s: string | null) => (s ? new Date(s).getTime() : 0);

function sortRows(rows: CheckRow[], key: SortKey, dir: 1 | -1) {
  const v = (r: CheckRow): number | string => {
    if (key === "env") return `${r.projectName} ${r.env}`.toLowerCase();
    if (key === "state") return ORDER[r.state];
    if (key === "used") return r.lastUsed.recorded ? time(r.lastUsed.lastRequestAt) : -1;
    if (key === "deploy") return time(r.lastDeploy.at);
    return r.uptime24h ?? -1;
  };
  return [...rows].sort((a, b) => {
    const x = v(a), y = v(b);
    return (x < y ? -1 : x > y ? 1 : 0) * dir || a.projectName.localeCompare(b.projectName) || a.env.localeCompare(b.env);
  });
}

function Th({ k, sort, setSort, children, className }: { k: SortKey; sort: [SortKey, 1 | -1]; setSort: (s: [SortKey, 1 | -1]) => void; children: string; className?: string }) {
  const on = sort[0] === k;
  return (
    <th className={className} aria-sort={on ? (sort[1] === 1 ? "ascending" : "descending") : "none"}>
      <button className="th-sort" onClick={() => setSort([k, on ? (sort[1] === 1 ? -1 : 1) : k === "used" || k === "deploy" ? -1 : 1])}>
        {children}{on ? (sort[1] === 1 ? " ↑" : " ↓") : ""}
      </button>
    </th>
  );
}

type Filter = "all" | "problems" | "lab";

export function ChecksPage() {
  const q = useChecks();
  const nav = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<[SortKey, 1 | -1]>(["state", 1]);
  const rows = q.data?.rows ?? [];
  const shown = useMemo(
    () => sortRows(rows.filter((r) => filter === "all" || (filter === "problems" ? r.state !== "up" : r.onLab)), sort[0], sort[1]),
    [rows, filter, sort],
  );
  const counts = { all: rows.length, problems: rows.filter((r) => r.state !== "up").length, lab: rows.filter((r) => r.onLab).length };
  const down = rows.filter((r) => r.state === "down").length;
  const degraded = rows.filter((r) => r.state === "degraded").length;
  const unprobed = rows.filter((r) => r.state === "up" && !r.probed).length;
  const coverage = unprobed ? ` ${unprobed} of them ${unprobed === 1 ? "is" : "are"} up only because ${unprobed === 1 ? "its server runs" : "their servers run"}: no URL is probed.` : "";

  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>Checks</h1>
          <p className="sub">
            {q.isPending ? <span className="skeleton" style={{ width: 360, height: 16 }} />
              : q.error ? "Couldn't read the checks."
              : down + degraded === 0 ? `No problem in ${rows.length} environments.${coverage} Every URL is probed once a minute.`
              : `${[down && `${down} down`, degraded && `${degraded} degraded`].filter(Boolean).join(", ")} of ${rows.length} environments.${coverage} Every URL is probed once a minute.`}
          </p>
        </div>
        {q.data && (
          <div className="actions">
            <span className="stamp">Checked {relative(q.data.checkedAt)}</span>
            <button className="btn" onClick={() => q.refetch()} disabled={q.isFetching}>{q.isFetching ? "Checking…" : "Refresh"}</button>
          </div>
        )}
      </div>

      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Show">
          {(["all", "problems", "lab"] as Filter[]).map((f) => (
            <button key={f} className="btn" aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {f === "all" ? "All" : f === "problems" ? "Problems" : "On the lab"} <span className="muted num">{counts[f]}</span>
            </button>
          ))}
        </div>
      </div>

      {q.error && <div className="panel error">{q.error.message} <button className="btn ghost" onClick={() => q.refetch()}>Retry</button></div>}

      <section className="panel flush">
        <div className="table-wrap">
          <table className="checks">
            <thead>
              <tr>
                <Th k="env" sort={sort} setSort={setSort}>Environment</Th>
                <Th k="state" sort={sort} setSort={setSort}>State</Th>
                <th className="hide-phone">URLs</th>
                <th className="num hide-phone">Containers</th>
                <Th k="used" sort={sort} setSort={setSort}>Last used</Th>
                <Th k="deploy" sort={sort} setSort={setSort} className="hide-phone">Last deploy</Th>
                <Th k="uptime" sort={sort} setSort={setSort} className="num">Uptime 24 h · 7 d</Th>
                <th className="hide-phone">Last 24 h</th>
              </tr>
            </thead>
            <tbody>
              {q.isPending && Array.from({ length: 6 }, (_, i) => (
                <tr key={i}>{[160, 120, 200, 40, 80, 90, 80, 150].map((w, j) => <td key={j} className={j === 2 || j === 3 || j === 5 || j === 7 ? "hide-phone" : ""}><span className="skeleton" style={{ width: w, height: 14 }} /></td>)}</tr>
              ))}
              {!q.isPending && shown.length === 0 && <tr><td colSpan={8}><div className="empty">{filter === "problems" ? "No problems: every environment answers." : "No environments."}</div></td></tr>}
              {shown.map((r) => (
                <tr key={r.envId} className="link" onClick={() => nav(`/p/${r.project}`)}>
                  <td>
                    <Link to={`/p/${r.project}`} className="cell-main" onClick={(e) => e.stopPropagation()}>{r.projectName}</Link>
                    <div className="cell-sub"><span className="mono">{r.env}</span> · {r.onLab ? "on the lab" : city(r.region)}</div>
                  </td>
                  <td style={{ maxWidth: 280 }}>
                    <StateMark state={r.state} />
                    {r.state === "up" && !r.probed && <span className="pill" style={{ marginLeft: 8 }} title="No URL or health check answered: only the server or containers are known to run">unprobed</span>}
                    {r.reasons.length > 0 && <div className="cell-sub">{r.reasons.slice(0, 3).join("; ")}{r.reasons.length > 3 ? `; ${r.reasons.length - 3} more` : ""}</div>}
                  </td>
                  <td className="hide-phone" style={{ maxWidth: 320 }}>
                    {r.urls.map((p) => <Probe key={p.url} p={p} />)}
                    {r.unprobed.map((u) => <div key={u} className="probe"><span className="mono truncate">{host(u)}</span><span className="muted">not probed yet</span></div>)}
                    {r.urls.length + r.unprobed.length === 0 && <span className="muted">no URL</span>}
                    {r.checks.length > 0 && <div className="cell-sub" style={{ marginTop: 6 }}>Health checks</div>}
                    {r.checks.map((c) => c.probe
                      ? <Probe key={c.url} p={c.probe} expect={c.expect} />
                      : <div key={c.url} className="probe"><span className="mono truncate">{host(c.url)}</span><span className="muted">not probed yet</span></div>)}
                  </td>
                  <td className="num hide-phone">{r.containers ? <span className={r.containers.healthy < r.containers.expected ? "error" : ""}>{r.containers.healthy} / {r.containers.expected}</span> : <span className="muted">—</span>}</td>
                  <td><LastUsed u={r.lastUsed} /></td>
                  <td className="hide-phone">
                    {r.lastDeploy.at ? <><div title={new Date(r.lastDeploy.at).toLocaleString()}>{relative(r.lastDeploy.at)}</div><div className="cell-sub mono">{r.lastDeploy.sha?.slice(0, 8) ?? ""}</div></> : <span className="muted">not from the lab</span>}
                  </td>
                  <td className="num">{pct(r.uptime24h)}<div className="cell-sub">{pct(r.uptime7d)}</div></td>
                  <td className="hide-phone"><Strip strip={r.strip} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <p className="stamp">
        Up: every URL answers below 500 with a trusted certificate, every health check answers its expected status (or below 400), and every container
        that should run is healthy. Unprobed: up only because the server or containers run; no URL or health check answered. Last used: the last request from a person in the
        lab's access log, without health checks, the lab's probes, bots and monitors; recorded for environments on the lab. Uptime: the share of probes that answered.
      </p>
    </div>
  );
}
